import React, { useState } from 'react';
import axios from 'axios';

/**
 * Gestion de l'abonnement côté élève : résilier / réactiver.
 * - Masqué pour l'offre 'essai' (non renouvelable).
 * - Si une résiliation est programmée (cancelled_at renseigné) : bandeau
 *   "actif jusqu'au X" + bouton "Réactiver".
 * - Sinon : bouton "Gérer mon abonnement" → modale de confirmation de résiliation.
 *
 * Props : token, abonnement (objet /credits/me), onDone (refresh crédits).
 */
export default function ResilierAbonnement({ token, abonnement, onDone }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Masqué si pas d'abonnement récurrent (l'essai ne se renouvelle pas)
  if (!abonnement || abonnement.type === 'essai') return null;

  const cancelPending = !!abonnement.cancelled_at;
  // Formatage robuste : si la date n'a pas d'info de fuseau (ex. "2026-08-01 22:09:48"),
  // on l'interprète en UTC pour coller à la date de fin de période Stripe.
  const fmt = (v) => {
    if (!v) return '';
    let s = String(v).replace(' ', 'T');
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
    try { return new Date(s).toLocaleDateString('fr-FR'); } catch { return ''; }
  };

  const call = async (path) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}${path}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      if (onDone) onDone();
      return true;
    } catch (e) {
      setError(e?.response?.data?.error || 'Une erreur est survenue. Réessayez.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const resilier = () => call('/api/stripe/cancel-subscription');
  const reactiver = async () => { await call('/api/stripe/reactivate-subscription'); };

  const close = () => { if (loading) return; setOpen(false); setResult(null); setError(null); };

  return (
    <div className="abo-wrap">
      {cancelPending ? (
        <div className="abo-banner">
          <span>
            🔔 Abonnement résilié — actif jusqu'au <strong>{fmt(abonnement.end_date)}</strong>. Il ne se renouvellera plus.
          </span>
          <button className="abo-btn-reactivate" onClick={reactiver} disabled={loading}>
            {loading ? '…' : 'Réactiver mon abonnement'}
          </button>
        </div>
      ) : (
        <button className="abo-btn-manage" onClick={() => setOpen(true)}>
          Gérer mon abonnement
        </button>
      )}

      {error && cancelPending && <div className="abo-error">{error}</div>}

      {open && (
        <div className="abo-overlay" onClick={close}>
          <div className="abo-modal" onClick={(e) => e.stopPropagation()}>
            {result ? (
              <>
                <h3>Résiliation prise en compte ✅</h3>
                <p>
                  Ton abonnement ne se renouvellera plus. Tu gardes ton accès et tes crédits
                  jusqu'à la fin de ta période déjà payée — la date exacte est indiquée dans le menu.
                </p>
                <div className="abo-actions">
                  <button className="abo-btn-primary" onClick={close}>Fermer</button>
                </div>
              </>
            ) : (
              <>
                <h3>Résilier l'abonnement ?</h3>
                <p>
                  Sans engagement. La résiliation prend effet à la <strong>fin de la période déjà payée</strong> :
                  tu conserves ton accès et tes crédits jusque-là, puis l'abonnement ne se renouvelle plus.
                  Aucun prélèvement supplémentaire.
                </p>
                {error && <p className="abo-error">{error}</p>}
                <div className="abo-actions">
                  <button className="abo-btn-secondary" onClick={close} disabled={loading}>
                    Garder mon abonnement
                  </button>
                  <button className="abo-btn-danger" onClick={resilier} disabled={loading}>
                    {loading ? 'Résiliation…' : 'Confirmer la résiliation'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        .abo-wrap{display:flex;flex-direction:column;align-items:flex-end;gap:6px}
        .abo-btn-manage{background:#fff;border:1px solid #d1d5db;color:#374151;font-size:13px;font-weight:600;border-radius:8px;cursor:pointer;padding:8px 14px}
        .abo-btn-manage:hover{background:#f3f4f6;border-color:#9ca3af}
        .abo-banner{display:flex;align-items:center;gap:12px;flex-wrap:wrap;justify-content:flex-end;background:#fff7ed;border:1px solid #fed7aa;color:#9a3412;border-radius:10px;padding:10px 14px;font-size:13px}
        .abo-btn-reactivate{background:#22c55e;border:none;color:#fff;font-size:13px;font-weight:600;border-radius:8px;cursor:pointer;padding:7px 12px}
        .abo-btn-reactivate:disabled{opacity:.6;cursor:default}
        .abo-error{color:#ef4444;font-size:13px}
        .abo-overlay{position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center;z-index:2000;padding:16px}
        .abo-modal{background:#fff;color:#111827;border-radius:14px;padding:24px;max-width:440px;width:100%;box-shadow:0 20px 60px rgba(0,0,0,.35);text-align:left}
        .abo-modal h3{margin:0 0 12px;font-size:18px}
        .abo-modal p{margin:0;line-height:1.5;color:#374151;font-size:14px}
        .abo-actions{display:flex;gap:10px;justify-content:flex-end;margin-top:20px;flex-wrap:wrap}
        .abo-btn-secondary{padding:9px 14px;border:1px solid #d1d5db;background:#fff;border-radius:9px;cursor:pointer;font-size:14px}
        .abo-btn-danger{padding:9px 14px;border:none;background:#ef4444;color:#fff;border-radius:9px;cursor:pointer;font-size:14px}
        .abo-btn-danger:disabled{opacity:.6}
        .abo-btn-primary{padding:9px 16px;border:none;background:#22c55e;color:#fff;border-radius:9px;cursor:pointer;font-size:14px}
      `}</style>
    </div>
  );
}
